import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {takeUntil} from 'rxjs/operators';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {FormField, FormSection, FormViewModeType} from '@core/interfaces/engin/maintenance-planning/form-visualization';
import {ButtonActionType} from '@core/interfaces/common/form';
import {BehaviorSubject, Observable} from 'rxjs';
import {groupBy} from 'lodash';
import {
    DeficiencyInspectionStatsDto,
    InspectionStatsDto,
} from '@core/interfaces/engin/maintenance-planning/deficiencies.model';
import {NbSidebarService} from '@nebular/theme';

@Component({
    selector: 'ngx-form-section',
    templateUrl: './form-section.component.html',
    styleUrls: ['./form-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormSectionComponent extends Unsubscribable implements OnInit, OnChanges {
    @Input() sections: FormSection[];
    @Input() continueEvent: number;
    @Input() buttonAction: ButtonActionType;
    @Input() viewMode: FormViewModeType;
    @Input() totalDeficiencies: InspectionStatsDto | null;
    @Input() totalInvalidFieldsList: Observable<number[]> = new BehaviorSubject<number[]>([]);
    @Input() selectedPath: {sectionId: number; subsectionId: number};
    @Output() selectionChanged = new EventEmitter<{sectionId: number; subsectionId: number}>();
    sectionStatusList: {label: string; validationPass: boolean; parentSection?: string; subParentSection?: string}[] =
        [];
    form: FormGroup = this.fb.group({
        sectionId: this.fb.control(-1),
        subsectionId: this.fb.control(-1),
    });

    constructor(private fb: FormBuilder, protected cd: ChangeDetectorRef, public sideBarService: NbSidebarService) {
        super();
    }

    ngOnInit(): void {
        this.totalInvalidFieldsList.pipe(takeUntil(this.unsubscribe$)).subscribe((totalInvalidFieldsList: number[]) => {
            this.invalidFieldsBySection(totalInvalidFieldsList);
            this.cd.detectChanges();
        });

        this.form.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
            this.selectionChanged.emit(this.form.value);
        });

        // Open first section/subsection by default
        if (this.form.value.sectionId === -1 && this.form.value.subsectionId === -1) {
            this.defaultFistSections();
        }
    }

    private invalidFieldsBySection(invalidFields: number[]): void {
        this.sectionStatusList = [];
        this.sections.map((section: FormSection) => {
            section.sections.map((subsection: FormSection) => {
                this.sectionsValidationCheck(subsection, invalidFields, section.label);
            });
        });

        const groupByParentSection = groupBy(this.sectionStatusList, 'parentSection');
        Object.keys(groupByParentSection).map((key) => {
            this.sectionStatusList.push({
                label: key,
                validationPass: groupByParentSection[key].every((status) => status.validationPass),
            });
        });
    }

    private sectionsValidationCheck(
        s: FormSection,
        invalidFieldIds: number[],
        parentLabel: string,
        subParentSection?: string,
    ): void {
        if (s.sections?.length > 0) {
            s.sections.forEach((sub: FormSection) => {
                this.sectionsValidationCheck(sub, invalidFieldIds, parentLabel, s.label);
            });
        }
        if (s.fields?.length > 0) {
            this.sectionStatusList.push({
                label: s.label,
                validationPass: this.checkFields(s.fields, invalidFieldIds),
                parentSection: parentLabel || null,
                subParentSection: subParentSection || null,
            });
        }
    }

    private checkFields(fields: FormField[], invalidFieldIds: number[]): boolean {
        return !fields.some((f: FormField) => invalidFieldIds.includes(f.id));
    }

    private countDeficienciesRecursively(section: FormSection): number {
        let count: number = 0;
        if (this.totalDeficiencies == null) return count;

        // Count deficiency in the current section if present
        count +=
            this.totalDeficiencies.stats
                .filter((s: DeficiencyInspectionStatsDto) => s.formSectionId === section.id)
                .reduce((a, b) => a + b.countOfDeficiency, 0) ?? 0;

        // Recursively count deficiencies in subsections
        for (const subSection of section.sections || []) {
            count += this.countDeficienciesRecursively(subSection);
        }

        return count;
    }

    public sectionHasDeficiency(section: FormSection): number {
        return this.countDeficienciesRecursively(section);
    }

    onOpenDeficiencySidebar(section: FormSection): void {
        const deficiencyStatForSection = this.totalDeficiencies.stats.filter((s) => s.formSectionId === section.id);
        // match on section and field id
        const sectionWithOnlyFieldDeficiencies = section.sections.filter(
            (section) =>
                section.sections.filter((section) =>
                    deficiencyStatForSection.find((s) => s.formSectionId === section.id),
                ) &&
                section.fields.filter((field) => field.id === this.totalDeficiencies.stats[0].formFieldId).length > 0,
        );
        // strip out other fields that don't match
        sectionWithOnlyFieldDeficiencies.forEach((section) => {
            section.fields = section.fields.filter((field) => field.id === this.totalDeficiencies.stats[0].formFieldId);
        });

        // we have only sections w/fields that are deficient

        // get list of deficiencies
        //this.maintenancePlanningService.getDeficienciesDtoList()

        // send that list to the side bar service as a bunch of deficiencies

        this.sideBarService.expand('deficiencies-details');
    }

    showRedDot(label: string): boolean {
        if (this.sectionStatusList.length > 0) {
            const section = this.sectionStatusList.find((item) => item.label === label);
            if (section) {
                return !section?.validationPass || false;
            } else {
                const sections = this.sectionStatusList.filter((item) => item.subParentSection === label);
                return !sections.every((item) => item.validationPass);
            }
        } else {
            return false;
        }
    }

    defaultFistSections() {
        this.form.setValue({
            sectionId: this.sections[0].id,
            subsectionId: this.sections[0].sections[0]?.id || -1,
        });
    }

    selectSection(sectionId, subsectionId): void {
        this.form.setValue({
            sectionId: sectionId,
            subsectionId: subsectionId,
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        //ToDo this method should be re-written to make it controllable + should be refactored
        if (changes.continueEvent) {
            const {sectionId, subsectionId} = this.form.value;
            const currentSection = this.sections.find((item) => item.id === sectionId);
            const currentSectionIndex = this.sections.findIndex((item) => item.id === sectionId);
            const currentSubSection = currentSection?.sections;
            const currentSubSectionIndex = currentSubSection?.findIndex((item) => item.id === subsectionId);

            const movedSubSectionIndex =
                currentSubSection?.findIndex((item) => item.id === this.moveAction(subsectionId)) === -1
                    ? this.moveAction(currentSubSectionIndex)
                    : currentSubSection?.findIndex((item) => item.id === this.moveAction(subsectionId));

            const startOfSection = 0 === movedSubSectionIndex;
            const endOfSubSection = movedSubSectionIndex === currentSubSection?.length;
            if (this.form.value.sectionId !== -1 && this.form.value.subsectionId !== -1) {
                //children section selected
                if (currentSubSection.length - 1 >= currentSubSectionIndex && !endOfSubSection && !startOfSection) {
                    this.form.setValue({
                        sectionId: currentSection.id,
                        subsectionId: currentSubSection[movedSubSectionIndex].id,
                    });
                    if (this.hasViewModeJustChangedToEditResults(changes.viewMode)) {
                        this.fromReviewMode(currentSubSectionIndex);
                    }
                } else if (endOfSubSection && this.buttonAction === ButtonActionType.Next) {
                    this.form.setValue({
                        sectionId: this.sections[this.moveAction(currentSectionIndex)]?.id,
                        subsectionId: this.sections[this.moveAction(currentSectionIndex)]?.sections[0]?.id,
                    });
                } else if (startOfSection && this.buttonAction === ButtonActionType.Prev) {
                    this.handlePreviousSectionNavigation(currentSectionIndex, currentSubSectionIndex);
                }
            } else if (this.form.value.sectionId !== -1 && this.form.value.subsectionId === -1) {
                //  code for level 1
                this.form.setValue({
                    sectionId: this.sections[this.moveAction(currentSectionIndex)]?.id || 1,
                    subsectionId: -1,
                });
                if (this.hasViewModeJustChangedToEditResults(changes.viewMode)) {
                    this.form.setValue({
                        sectionId: this.sections[currentSectionIndex]?.id || 1,
                        subsectionId: -1,
                    });
                }
            }
        }

        if (changes.selectedPath && changes.selectedPath.currentValue !== changes.selectedPath.previousValue) {
            this.selectSection(this.selectedPath.sectionId, this.selectedPath.subsectionId);
        }
    }
    fromReviewMode(currentSubSectionIndex: number): void {
        const {sectionId} = this.form.value;
        const currentSection = this.sections.find((item) => item.id === sectionId);
        const currentSubSection = currentSection?.sections;
        this.form.setValue({
            sectionId: currentSection.id,
            subsectionId: currentSubSection[currentSubSectionIndex].id,
        });
    }
    private handlePreviousSectionNavigation(currentSectionIndex: number, currentSubSectionIndex: number): void {
        let nextSectionIndex =
            currentSubSectionIndex === 0 ? this.moveAction(currentSectionIndex) : currentSectionIndex;
        const sectionList = this.sections[nextSectionIndex].sections;
        const subSectionId =
            currentSectionIndex !== 0 && currentSubSectionIndex === 0
                ? sectionList[sectionList.length - 1]?.id
                : sectionList[0]?.id;

        this.form.setValue({
            sectionId: this.sections[nextSectionIndex].id,
            subsectionId: subSectionId,
        });
    }

    private hasViewModeJustChangedToEditResults(viewModeChange): boolean {
        return (
            viewModeChange?.previousValue === FormViewModeType.VIEW_RESULTS &&
            viewModeChange?.currentValue === FormViewModeType.EDIT_RESULTS
        );
    }

    private moveAction(value) {
        if (this.buttonAction === ButtonActionType.Next) {
            return value + 1;
        } else if (this.buttonAction === ButtonActionType.Prev) {
            return value === 0 ? 0 : value - 1;
        }
    }
}
